import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, UploadCloud, Loader2, Trash2, Eye, ScanLine, Camera } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { value: 'passport_front', label: 'Passport (Front)' },
  { value: 'passport_back', label: 'Passport (Back)' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'aadhar', label: 'Aadhar Card' },
  { value: 'visa', label: 'Visa' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'voter_id', label: 'Voter ID' },
];

const DOC_LABELS = {
  passport: 'Passport',
  passport_front: 'Passport Front',
  passport_back: 'Passport Back',
  pan: 'PAN',
  aadhar: 'Aadhaar',
  voter_id: 'Voter ID',
  driving_license: 'Driving Licence',
  visa: 'Visa',
};

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp';
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Documents section embedded inside TravellerDetail.
 * - Lists existing documents (passport, PAN, etc.)
 * - Upload new document → optional OCR → optional auto-fill of traveller fields
 */
export default function TravellerDocuments({ ucc, onAutofill, familyMembers = [], onFamilyChange }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('passport_front');
  const [runOcr, setRunOcr] = useState(true);
  // Task 2 — which person this upload belongs to: 'main' or a family member
  // index. Routing a family doc through the family endpoint stops the main
  // traveller's fields being overwritten by a relative's passport OCR.
  const [target, setTarget] = useState('main');
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const fam = Array.isArray(familyMembers) ? familyMembers : [];
  const isFamily = target !== 'main';
  const memberIdx = isFamily ? Number(target) : -1;

  const fetchDocs = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/clients/${ucc}/documents`, { withCredentials: true });
      setDocs(Array.isArray(r.data) ? r.data : (r.data?.documents || []));
    } catch (err) {
      // 404 or 401 means no docs yet — safe to silently render empty list
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [ucc]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handlePick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error('File too large (max 10 MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      fd.append('run_ocr_extraction', String(runOcr));
      const url = isFamily
        ? `${BACKEND_URL}/api/clients/${ucc}/family/${memberIdx}/documents/upload`
        : `${BACKEND_URL}/api/clients/${ucc}/documents/upload`;
      const r = await axios.post(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      toast.success(runOcr ? 'Document uploaded · OCR extracted' : 'Document uploaded');
      // Only auto-fill the MAIN traveller from a MAIN-traveller upload. Family
      // uploads must never touch the main traveller's fields.
      if (!isFamily && r.data?.extracted_data && onAutofill) onAutofill(r.data.extracted_data, docType);
      if (isFamily) { onFamilyChange && onFamilyChange(); } else { fetchDocs(); }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Upload failed';
      toast.error(typeof msg === 'string' ? msg : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
      if (cameraRef.current) cameraRef.current.value = '';
    }
  };

  const handleDelete = async (storagePath) => {
    if (!storagePath) { toast.error('Cannot delete this document'); return; }
    if (!window.confirm('Delete this document?')) return;
    try {
      const url = isFamily
        ? `${BACKEND_URL}/api/clients/${ucc}/family/${memberIdx}/documents`
        : `${BACKEND_URL}/api/clients/${ucc}/documents`;
      await axios.delete(url, { params: { storage_path: storagePath }, withCredentials: true });
      toast.success('Document deleted');
      if (isFamily) { onFamilyChange && onFamilyChange(); } else { fetchDocs(); }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Docs shown depend on the selected person. Family docs come from the
  // traveller record's familyMembers[i].documents array (filtered, array-only).
  const shownDocs = isFamily
    ? (Array.isArray(fam[memberIdx]?.documents) ? fam[memberIdx].documents : [])
        .filter((d) => d && typeof d === 'object' && !d.is_deleted && d.storage_path)
    : docs;

  const docLabel = (t) => DOC_LABELS[t] || DOC_TYPES.find((d) => d.value === t)?.label || t;

  // BATCH 1 — no permanent/public URLs. Ask the backend to mint a short-lived
  // HMAC-signed URL (record + field-level permission checked + audited), then
  // open it. Open a blank tab synchronously first so the popup isn't blocked
  // by the async fetch.
  const openSigned = async (storagePath) => {
    if (!storagePath) { toast.error('Cannot open this document'); return; }
    const w = window.open('', '_blank');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/documents/sign`, {
        params: { storage_path: storagePath }, withCredentials: true,
      });
      const url = `${BACKEND_URL}${r.data.url}`;
      if (w) w.location = url; else window.open(url, '_blank');
    } catch (err) {
      if (w) w.close();
      const msg = err?.response?.data?.detail || 'You do not have access to view this document';
      toast.error(typeof msg === 'string' ? msg : 'Cannot view document');
    }
  };

  return (
    <Card className="p-6 rounded-2xl" data-testid="traveller-documents-card">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-teal-600" />
          <h2 className="text-lg font-bold text-slate-900">Documents</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {fam.length > 0 && (
            <Select value={String(target)} onValueChange={setTarget}>
              <SelectTrigger className="w-44 h-9" data-testid="doc-belongs-to-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="main">Main traveller</SelectItem>
                {fam.map((m, i) => (
                  <SelectItem key={m?._id || i} value={String(i)}>
                    {(m?.fullName || `Member ${i + 1}`) + (m?.relationship ? ` · ${m.relationship}` : '')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={docType} onValueChange={setDocType}>
            <SelectTrigger className="w-40 h-9" data-testid="doc-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map((d) => (
                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 select-none">
            <input
              type="checkbox"
              checked={runOcr}
              onChange={(e) => setRunOcr(e.target.checked)}
              data-testid="doc-ocr-toggle"
            />
            <ScanLine className="w-3.5 h-3.5" /> OCR + auto-fill
          </label>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            onChange={handleFile}
            className="hidden"
            data-testid="doc-file-input"
          />
          {/* Mobile camera capture — opens the rear camera to scan a doc. */}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFile}
            className="hidden"
            data-testid="doc-camera-input"
          />
          <Button
            onClick={() => cameraRef.current?.click()}
            disabled={uploading}
            variant="outline"
            className="md:hidden h-11 border-teal-200 text-teal-700"
            data-testid="scan-document-btn"
          >
            <Camera className="w-4 h-4 mr-1.5" /> Scan
          </Button>
          <Button
            onClick={handlePick}
            disabled={uploading}
            className="bg-teal-600 hover:bg-teal-700 text-white h-11 md:h-9"
            data-testid="upload-document-btn"
          >
            {uploading
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Uploading…</>
              : <><UploadCloud className="w-4 h-4 mr-1.5" /> Upload</>}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-6 text-center">Loading documents…</div>
      ) : shownDocs.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center border-2 border-dashed border-slate-200 rounded-xl">
          {isFamily
            ? 'No documents uploaded for this family member yet.'
            : 'No documents yet. Upload passport, PAN, Aadhar, visa, or DL to auto-fill traveller details.'}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="document-list">
          {shownDocs.map((d, idx) => (
            <li key={d.storage_path || idx} className="flex items-center justify-between py-3 gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-teal-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {d.display_name || docLabel(d.doc_type)}
                    {d.extracted_data?.passportNumber ? ` · ${d.extracted_data.passportNumber}` : ''}
                    {d.extracted_data?.panCard ? ` · ${d.extracted_data.panCard}` : ''}
                    {d.extracted_data?.aadharCard ? ` · ${d.extracted_data.aadharCard}` : ''}
                  </div>
                  <div className="text-xs text-slate-500">
                    {d.original_filename || 'document'}
                    {d.uploaded_at ? ` · ${new Date(d.uploaded_at).toLocaleString('en-IN')}` : ''}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {d.storage_path && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openSigned(d.storage_path)}
                    data-testid={`view-document-${idx}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(d.storage_path)}
                  className="text-red-600 hover:text-red-700"
                  data-testid={`delete-document-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
